import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import {
  useSortable,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { 
  Plus, 
  Edit, 
  Trash2,
  Users,
  CheckCircle,
  FileText,
  BarChart3,
  UserCheck,
  XCircle,
  Target,
  GripVertical,
  Settings,
  Mail,
  Calendar,
  Bell,
  FileCheck,
  RefreshCw,
  Handshake,
  ArrowLeft,
  Layers
} from 'lucide-react'
import { Button } from 'components/ui/Button'
import { Card, CardContent, CardHeader, CardTitle } from 'components/ui/Card'
import { PageHeader } from 'components/layout/PageHeader'
import { Badge } from 'components/ui/Badge'
import { Modal } from 'components/ui/Modal'
import { Input } from 'components/ui/Input'
import { Textarea } from 'components/ui/Textarea'
import { useToast } from 'contexts/ToastContext'
import { useAuth } from 'contexts/AuthContext'
import { hiringProcessApi, HiringProcessTemplate, HiringStep, StepAction } from 'lib/api'
import { StepActionModal } from 'features/hiring/components/StepActionModal'
import { HiringStepModal } from 'features/hiring/components/HiringStepModal'
import { DEFAULT_STEP_COLOR, STEP_COLORS, DEFAULT_STEP_TYPE, getUserAddStepCategoryOptions, isDuplicateNewStep, isResolvedStepCustom, buildStepPositionOptions } from 'constants/hiringProcess'

interface EditableStep extends Omit<HiringStep, 'actions'> {
  isEditing?: boolean
  actions: StepAction[]
}

// Helper functions
const getStepIcon = (stepType: string) => {
  const icons = {
    'initial': FileText,
    'scanning': Target,
    'screening': UserCheck,
    'interview': Users,
    'hr_interview': Handshake,
    'assessment': BarChart3,
    'offer': CheckCircle,
    'hired': CheckCircle,
    'rejected': XCircle,
    'reference_check': Users,
    'onboarding': Target,
    'reboot_interview': RefreshCw
  }
  return icons[stepType as keyof typeof icons] || Target
}

function TemplatePreviewCard({
  template,
  onSelect,
}: {
  template: HiringProcessTemplate
  onSelect: (id: number) => void
}) {
  const { t } = useTranslation()
  const isDefault = template.is_default
  const previewSteps = [...(template.steps || [])]
    .filter((s) => s.is_active !== false)
    .sort((a, b) => (a.order || 0) - (b.order || 0))

  return (
    <button
      type="button"
      onClick={() => onSelect(template.id)}
      className={`group flex h-full w-full flex-col gap-3 rounded-xl border p-4 text-left transition-all duration-200 ${
        isDefault
          ? 'border-muted-foreground/20 bg-muted/40 hover:bg-muted/55'
          : 'border-border bg-card hover:border-primary/35 hover:shadow-md'
      }`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <div
              className={`h-2.5 w-2.5 flex-shrink-0 rounded-full ${isDefault ? 'bg-muted-foreground/50' : ''}`}
              style={isDefault ? undefined : { backgroundColor: template.color || DEFAULT_STEP_COLOR }}
            />
            <h3
              className={`truncate text-sm font-semibold ${
                isDefault ? 'text-muted-foreground' : 'text-foreground'
              }`}
            >
              {template.name}
            </h3>
          </div>
          {template.description ? (
            <p className="mt-1 line-clamp-2 text-xs text-muted-foreground">{template.description}</p>
          ) : null}
        </div>
        {isDefault && (
          <Badge
            variant="outline"
            className="flex-shrink-0 border-muted-foreground/30 bg-muted/60 text-[10px] uppercase tracking-wide text-muted-foreground"
          >
            {t('common.default', 'Default')}
          </Badge>
        )}
      </div>

      <div className="flex flex-1 flex-col gap-1.5">
        {previewSteps.length === 0 ? (
          <div className="flex flex-1 items-center justify-center rounded-lg border border-dashed border-muted-foreground/25 bg-background/40 px-3 py-6 text-center">
            <p className="text-xs text-muted-foreground">{t('hiringProcess.noStepsInTemplate', 'No steps yet')}</p>
          </div>
        ) : (
          previewSteps.map((step, index) => {
            const StepIcon = getStepIcon(step.step_type)
            return (
              <div
                key={step.id ?? `${step.step_type}-${index}`}
                className={`flex items-center gap-2 rounded-lg border px-2.5 py-2 ${
                  isDefault
                    ? 'border-muted-foreground/15 bg-background/30'
                    : 'border-border/60 bg-muted/20'
                }`}
              >
                <div
                  className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full text-[10px] font-semibold text-primary-foreground"
                  style={{ backgroundColor: getStepColor(step.color) }}
                >
                  {index + 1}
                </div>
                <StepIcon className={`h-3.5 w-3.5 flex-shrink-0 ${isDefault ? 'text-muted-foreground/70' : 'text-muted-foreground'}`} />
                <span
                  className={`min-w-0 flex-1 truncate text-xs font-medium ${
                    isDefault ? 'text-muted-foreground' : 'text-foreground'
                  }`}
                >
                  {step.name || t(`hiringProcess.stepTypes.${step.step_type}`)}
                </span>
              </div>
            )
          })
        )}
      </div>

      <div className={`flex items-center justify-between text-[11px] ${isDefault ? 'text-muted-foreground/80' : 'text-muted-foreground'}`}>
        <span>
          {template.step_count ?? previewSteps.length}{' '}
          {(template.step_count ?? previewSteps.length) === 1
            ? t('hiringProcess.stepSingular', 'step')
            : t('hiringProcess.stepPlural', 'steps')}
        </span>
        <span className={`opacity-0 transition-opacity group-hover:opacity-100 ${isDefault ? 'text-muted-foreground' : 'text-primary'}`}>
          {t('hiringProcess.clickToManage', 'Manage →')}
        </span>
      </div>
    </button>
  )
}

const getStepColor = (color: string) => {
  return color || '#6B7280'
}

// Steps that cannot be modified (locked steps)
const IMMUTABLE_STEP_TYPES = ['initial', 'scanning']

const isStepImmutable = (stepType: string): boolean => {
  return IMMUTABLE_STEP_TYPES.includes(stepType)
}

const getActionIcon = (actionType: string) => {
  const icons = {
    'email': Mail,
    'send_email': Mail,
    'calendar': Calendar,
    'schedule_meeting': Calendar,
    'notification': Bell,
    'send_notification': Bell,
    'document': FileCheck,
    'generate_document': FileCheck,
    'evaluation': FileText,
    'approval': CheckCircle,
    'integration': Settings,
    'custom': Settings,
    'update_status': RefreshCw
  }
  const IconComponent = icons[actionType as keyof typeof icons] || Settings
  return <IconComponent className="h-3 w-3" />
}

// Helper to ensure actions is always an array
const ensureActionsArray = (actions: any): StepAction[] => {
  if (Array.isArray(actions)) {
    return actions
  }
  return []
}

// Sortable Step Component
function SortableStep({ 
  step, 
  index, 
  onEdit, 
  onDelete, 
  onAddAction,
  onEditAction,
  onDeleteAction,
  canEdit = true,
  canReorder = true
}: {
  step: EditableStep
  index: number
  onEdit: (index: number) => void
  onDelete: (index: number) => void
  onAddAction: (stepId: number) => void
  onEditAction: (stepId: number, action: StepAction) => void
  onDeleteAction: (stepId: number, actionId: number) => void
  canEdit?: boolean
  canReorder?: boolean
}) {
  const { t } = useTranslation()
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: step.id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  }

  const stepActions = ensureActionsArray(step.actions)
  const actionsCount = stepActions.length
  const isMandatory = Boolean(step.is_mandatory)
  const isInactive = step.is_active === false
  const isImmutable = isStepImmutable(step.step_type)
  const canDelete = step.is_deletable !== false
  const isDragLocked = isImmutable || !canReorder

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex flex-col gap-3 p-4 border rounded-xl bg-card transition-shadow duration-200 max-[1149px]:p-3 max-[1149px]:gap-2 ${
        isDragging ? 'opacity-60' : 'hover:shadow-sm'
      } ${isInactive ? 'opacity-60 border-dashed' : ''}`}
    >
      <div className="flex items-start gap-3 max-[1149px]:gap-2">
        <div
          className={`text-muted-foreground transition-colors h-8 w-8 flex items-center justify-center rounded-md bg-muted flex-shrink-0 max-[1149px]:h-7 max-[1149px]:w-7 ${
            isDragLocked ? 'cursor-not-allowed opacity-50' : 'cursor-move hover:text-foreground'
          }`}
          {...(isDragLocked ? {} : { ...attributes, ...listeners })}
          title={isImmutable ? t('hiringProcess.tooltips.immutableStep', 'This step cannot be modified') : t('common.drag')}
        >
          <GripVertical className="h-4 w-4 max-[1149px]:h-3.5 max-[1149px]:w-3.5" />
        </div>
        <div className="flex items-center justify-between flex-1 gap-4 max-[1149px]:gap-2 max-[1149px]:flex-col max-[1149px]:items-stretch">
          <div className="flex items-start gap-3 flex-1 min-w-0 max-[1149px]:gap-2">
            <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full text-xs font-semibold text-primary-foreground max-[1149px]:h-8 max-[1149px]:w-8 max-[1149px]:text-[10px]"
              style={{ backgroundColor: getStepColor(step.color) }}>
              {index + 1}
            </div>
            <div className="flex flex-col gap-2 flex-1 min-w-0 max-[1149px]:gap-1.5">
              <div className="flex flex-wrap items-center gap-2 max-[1149px]:gap-1.5">
                <h4 className="text-sm font-semibold text-foreground truncate max-[1149px]:text-xs">{step.name || t(`hiringProcess.stepTypes.${step.step_type}`)}</h4>
                {isImmutable && (
                  <Badge variant="outline" className="uppercase text-[10px] tracking-wide border-orange-300 dark:border-orange-700 text-orange-700 dark:text-orange-400 bg-orange-50 dark:bg-orange-900/20 max-[1149px]:text-[9px] max-[1149px]:px-1.5 max-[1149px]:py-0.5">
                    {t('hiringProcess.badges.locked', 'Locked')}
                  </Badge>
                )}
                {isInactive && (
                  <Badge variant="outline" className="uppercase text-[10px] tracking-wide border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 max-[1149px]:text-[9px] max-[1149px]:px-1.5 max-[1149px]:py-0.5">
                    {t('hiringProcess.badges.inactive', 'Inactive')}
                  </Badge>
                )}
                {isMandatory && !isInactive && !isImmutable && (
                  <Badge variant="outline" className="uppercase text-[10px] tracking-wide border-blue-200 dark:border-blue-800 text-blue-600 dark:text-blue-400 max-[1149px]:text-[9px] max-[1149px]:px-1.5 max-[1149px]:py-0.5">
                    {t('hiringProcess.badges.mandatory', 'Required')}
                  </Badge>
                )}
                <Badge variant="outline" className="text-[10px] max-[1149px]:text-[9px] max-[1149px]:px-1.5 max-[1149px]:py-0.5">
                  {t(`hiringProcess.stepTypes.${step.step_type}`)}
                </Badge>
                <Badge variant="secondary" className="text-[10px] max-[1149px]:text-[9px] max-[1149px]:px-1.5 max-[1149px]:py-0.5">
                  {actionsCount}{' '}
                  {actionsCount <= 1
                    ? t('hiringProcess.actionWordSingular', 'action')
                    : t('hiringProcess.actionWordPlural', 'actions')}
                </Badge>
              </div>
              {(() => {
                const translatedDescription = t(`hiringProcess.stepDescriptions.${step.step_type}`)
                return translatedDescription && (
                <p className="text-xs text-muted-foreground line-clamp-2 max-[1149px]:text-[10px]">
                    {translatedDescription}
                </p>
                )
              })()}
              {actionsCount > 0 && (
                <div className="flex flex-wrap gap-2 max-[1149px]:gap-1.5">
                  {stepActions.map((action) => (
                    <div
                      key={action.id}
                      className="inline-flex items-center gap-2 rounded-lg border border-muted bg-muted/40 px-2 py-1 text-[11px] max-[1149px]:px-1.5 max-[1149px]:py-0.5 max-[1149px]:text-[10px] max-[1149px]:gap-1"
                    >
                      {getActionIcon(action.action_type)}
                      <span className="font-medium truncate max-w-[140px] max-[1149px]:max-w-[100px]">{action.name}</span>
                      <Badge variant="outline" className="text-[10px] px-1.5 py-0.5 max-[1149px]:text-[9px] max-[1149px]:px-1 max-[1149px]:py-0">
                        {action.trigger_type}
                      </Badge>
                      {canEdit && (
                      <div className="flex items-center gap-1 max-[1149px]:gap-0.5">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => onEditAction(step.id, action)}
                          className="h-5 w-5 p-0 max-[1149px]:h-4 max-[1149px]:w-4"
                          title={t('common.edit')}
                        >
                          <Edit className="h-3 w-3 max-[1149px]:h-2.5 max-[1149px]:w-2.5" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => onDeleteAction(step.id, action.id)}
                          className="h-5 w-5 p-0 text-red-600 hover:text-red-700 max-[1149px]:h-4 max-[1149px]:w-4"
                          title={t('common.delete')}
                        >
                          <Trash2 className="h-3 w-3 max-[1149px]:h-2.5 max-[1149px]:w-2.5" />
                        </Button>
                      </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
              {actionsCount === 0 && (
                <div className="text-xs text-muted-foreground italic max-[1149px]:text-[10px]">
                  {t('hiringProcess.noActionsYet')}
                </div>
              )}
            </div>
          </div>
          {canEdit && (
          <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-end gap-2 max-[1149px]:flex-col max-[1149px]:w-full max-[1149px]:gap-2 max-[1149px]:mt-2">
            <div className="flex items-center gap-2 max-[1149px]:w-full max-[1149px]:justify-between">
              <Button
                size="sm"
                variant="outline"
                onClick={() => onEdit(index)}
                disabled={isImmutable}
                className="flex items-center gap-1 disabled:text-muted-foreground max-[1149px]:flex-1 max-[1149px]:h-9 max-[1149px]:text-sm max-[1149px]:justify-center"
                title={
                  isImmutable
                    ? t('hiringProcess.tooltips.immutableStep', 'This step cannot be modified')
                    : t('common.edit')
                }
              >
                <Edit className="h-3 w-3 max-[1149px]:h-3.5 max-[1149px]:w-3.5" />
                <span className="max-[1149px]:text-sm">{t('common.edit')}</span>
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => onDelete(index)}
                className="flex items-center gap-1 text-red-600 hover:text-red-700 disabled:text-muted-foreground max-[1149px]:flex-1 max-[1149px]:h-9 max-[1149px]:text-sm max-[1149px]:justify-center"
                title={
                  isImmutable
                    ? t('hiringProcess.tooltips.immutableStep', 'This step cannot be deleted')
                    : !canDelete
                    ? t('hiringProcess.tooltips.cannotDeleteMandatory', 'Default steps cannot be deleted')
                    : t('common.delete')
                }
                disabled={!canDelete || isImmutable}
              >
                <Trash2 className="h-3 w-3 max-[1149px]:h-3.5 max-[1149px]:w-3.5" />
                <span className="max-[1149px]:text-sm">{t('common.delete')}</span>
              </Button>
            </div>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => onAddAction(step.id)}
              className="flex items-center gap-1 max-[1149px]:w-full max-[1149px]:h-9 max-[1149px]:text-sm max-[1149px]:justify-center"
            >
              <Plus className="h-3 w-3 max-[1149px]:h-3.5 max-[1149px]:w-3.5" />
              <span className="max-[1149px]:text-sm">{t('hiringProcess.addAction')}</span>
            </Button>
          </div>
          )}
        </div>
      </div>
    </div>
  )
}

export function HiringProcess() {
  const { t, i18n } = useTranslation()
  const { addToast } = useToast()
  const { hasPermission } = useAuth()
  const canManageTemplates = hasPermission('manage_hiring_templates')
  const canManageSteps = hasPermission('manage_hiring_steps')
  const canCreateSteps = canManageSteps || hasPermission('create_hiring_steps')
  const canReorderSteps = canManageSteps || hasPermission('reorder_hiring_steps')
  const canChangePipeline = canCreateSteps || canReorderSteps
  
  const [templates, setTemplates] = useState<HiringProcessTemplate[]>([])
  const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(null)
  const [showTemplateModal, setShowTemplateModal] = useState(false)
  const [savingTemplate, setSavingTemplate] = useState(false)
  const [templateWizardStep, setTemplateWizardStep] = useState<1 | 2>(1)
  const [showWizardStepModal, setShowWizardStepModal] = useState(false)
  const [wizardEditingIndex, setWizardEditingIndex] = useState<number | null>(null)
  const [templateForm, setTemplateForm] = useState({
    name: '',
    description: '',
    color: '#3B82F6',
    is_active: true,
    is_default: false,
  })
  const [newTemplateSteps, setNewTemplateSteps] = useState<
    Array<{
      name: string
      description: string
      step_type: string
      color: string
      is_required: boolean
      estimated_duration: number
      is_active: boolean
    }>
  >([])

  const [template, setTemplate] = useState<HiringProcessTemplate | null>(null)
  const [steps, setSteps] = useState<EditableStep[]>([])
  const [loading, setLoading] = useState(true)
  const [loadingDetail, setLoadingDetail] = useState(false)
  const [editingStepIndex, setEditingStepIndex] = useState<number | null>(null)
  const [showStepModal, setShowStepModal] = useState(false)
  const [stepDeleteTarget, setStepDeleteTarget] = useState<EditableStep | null>(null)
  const [stepDeleteLoading, setStepDeleteLoading] = useState(false)
  const [showDeleteTemplateModal, setShowDeleteTemplateModal] = useState(false)
  const [deletingTemplate, setDeletingTemplate] = useState<HiringProcessTemplate | null>(null)

  // Action management state
  const [showActionModal, setShowActionModal] = useState(false)
  const [editingAction, setEditingAction] = useState<StepAction | null>(null)
  const [currentStepId, setCurrentStepId] = useState<number | null>(null)
  const [actionDeleteTarget, setActionDeleteTarget] = useState<{ stepId: number, action: StepAction } | null>(null)
  const [actionDeleteLoading, setActionDeleteLoading] = useState(false)

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  useEffect(() => {
    loadTemplates()
  }, [i18n.language])

  useEffect(() => {
    if (selectedTemplateId != null) {
      loadTemplate(selectedTemplateId)
    }
  }, [selectedTemplateId, i18n.language])

  const loadTemplates = async (options?: { silent?: boolean }) => {
    try {
      if (!options?.silent) {
        setLoading(true)
      }
      const res = await hiringProcessApi.getTemplates()
      const items = Array.isArray(res.data) ? res.data : ((res.data as any).results || [])
      const active = items.filter((tpl: HiringProcessTemplate) => tpl.is_active)
      setTemplates(active)
    } catch (error) {
      const anyError: any = error
      const backendMessage =
        anyError?.response?.data?.message ||
        anyError?.response?.data?.detail ||
        anyError?.message

      addToast({
        type: 'error',
        title: t('common.error'),
        message: t('hiringProcess.messages.templateLoadError', { error: backendMessage || 'Unknown error' }),
      })

      setTemplates([])
      setSelectedTemplateId(null)
      setTemplate(null)
      setSteps([])
    } finally {
      if (!options?.silent) {
        setLoading(false)
      }
    }
  }

  const sortedTemplatesForBrowse = [...templates].sort((a, b) => {
    if (a.is_default !== b.is_default) {
      return a.is_default ? -1 : 1
    }
    return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
  })

  const handleSelectTemplate = (templateId: number) => {
    setSelectedTemplateId(templateId)
  }

  const handleBackToBrowse = () => {
    setSelectedTemplateId(null)
    setTemplate(null)
    setSteps([])
    setEditingStepIndex(null)
    setShowStepModal(false)
    loadTemplates({ silent: true })
  }

  const loadTemplate = async (templateId: number) => {
    try {
      setLoadingDetail(true)
      const response = await hiringProcessApi.getTemplate(templateId)
      const resolvedTemplate = response.data

      // Backend may fork the legacy global default into an org-owned copy on first access.
      if (resolvedTemplate.id !== templateId) {
        const templatesRes = await hiringProcessApi.getTemplates()
        const items = Array.isArray(templatesRes.data)
          ? templatesRes.data
          : ((templatesRes.data as any).results || [])
        const active = items.filter((tpl: HiringProcessTemplate) => tpl.is_active)
        setTemplates(active)
        setSelectedTemplateId(resolvedTemplate.id)
      }

      setTemplate(resolvedTemplate)
      const allSteps = resolvedTemplate.steps || []
      
      // Sort steps by order to ensure correct display order (backend should already sort, but double-check)
      const sortedSteps = [...allSteps].sort((a, b) => (a.order || 0) - (b.order || 0))
      
      // Keep all steps (including inactive ones) - they should be visible in management
      // Note: Inactive steps won't appear in the kanban/workflow, but are still editable here
      
      // Fetch actions for each step and ensure actions is always an array
      const stepsWithActions = await Promise.all(
        sortedSteps.map(async (step: HiringStep) => {
          try {
            const actionsResponse = await hiringProcessApi.getActions(step.id)
            
            // Handle different response formats (direct array or wrapped in data)
            let actions: StepAction[] = []
            const responseData = actionsResponse.data as any
            if (Array.isArray(responseData)) {
              actions = ensureActionsArray(responseData)
            } else if (responseData?.results && Array.isArray(responseData.results)) {
              // Handle paginated response
              actions = ensureActionsArray(responseData.results)
            } else if (Array.isArray(actionsResponse)) {
              // Response is directly an array
              actions = ensureActionsArray(actionsResponse as any)
            }
            
            return {
              ...step,
              actions,
              action_count: actions.length
            }
          } catch (error) {
            // Fallback to actions from step if API call fails
            const actions = ensureActionsArray(step.actions)
            return {
              ...step,
              actions,
              action_count: actions.length
            }
          }
        })
      )
      setSteps(stepsWithActions)
    } catch (error) {
      const anyError: any = error
      const backendMessage =
        anyError?.response?.data?.message ||
        anyError?.response?.data?.detail ||
        anyError?.message

      addToast({
        type: 'error',
        title: t('common.error'),
        message: t('hiringProcess.messages.templateLoadError', { error: backendMessage || 'Unknown error' }),
      })
      setTemplate(null)
      setSteps([])
    } finally {
      setLoadingDetail(false)
    }
  }

  const handleOpenCreateTemplate = () => {
    setTemplateForm({
      name: '',
      description: '',
      color: '#3B82F6',
      is_active: true,
      is_default: false,
    })
    // Initialize wizard step
    setTemplateWizardStep(1)
    // Pre-populate with the three core steps from the default pipeline
    const coreStepTypes: string[] = ['initial', 'scanning', 'screening']
    setNewTemplateSteps(
      coreStepTypes.map((type, index) => ({
        step_type: type,
        name: t(`hiringProcess.stepTypes.${type}`),
        description: t(`hiringProcess.stepDescriptions.${type}`),
        color: STEP_COLORS[index] || '#6B7280',
        is_required: true,
        estimated_duration: 1,
        is_active: true,
      }))
    )
    setShowTemplateModal(true)
  }

  const handleDeleteTemplateClick = () => {
    if (template && !template.is_default) {
      setDeletingTemplate(template)
      setShowDeleteTemplateModal(true)
    }
  }

  const handleConfirmDeleteTemplate = async () => {
    if (!deletingTemplate) return
    try {
      await hiringProcessApi.deleteTemplate(deletingTemplate.id)
      addToast({
        type: 'success',
        title: t('common.success'),
        message: t('hiringProcess.messages.templateDeleted', 'Template deleted'),
      })
      setShowDeleteTemplateModal(false)
      setDeletingTemplate(null)
      handleBackToBrowse()
    } catch (error: unknown) {
      const anyError = error as { response?: { data?: { detail?: string } }; message?: string }
      const msg =
        anyError?.response?.data?.detail ||
        anyError?.message ||
        t('hiringProcess.messages.templateDeleteError', 'Failed to delete template')
      addToast({ type: 'error', title: t('common.error'), message: msg })
    }
  }

  const handleCreateTemplate = async () => {
    if (!templateForm.name.trim()) {
      addToast({ type: 'error', title: t('common.error'), message: t('hiringProcess.form.nameRequired', 'Name is required') })
      return
    }

    try {
      setSavingTemplate(true)
      const createdRes = await hiringProcessApi.createTemplate({
        ...templateForm,
        name: templateForm.name.trim(),
        description: templateForm.description?.trim() || '',
        steps: newTemplateSteps.map((step, index) => ({
          name: step.name,
          description: step.description,
          step_type: step.step_type,
          order: index + 1,
          color: step.color,
          is_required: step.is_required,
          estimated_duration: step.estimated_duration,
          is_active: step.is_active,
        })),
      })

      if (createdRes.data?.id && templateForm.is_default) {
        const others = templates.filter(tpl => tpl.is_default && tpl.id !== createdRes.data.id)
        await Promise.all(
          others.map(tpl => hiringProcessApi.updateTemplate(tpl.id, { is_default: false }))
        )
      }

      setShowTemplateModal(false)
      await loadTemplates({ silent: true })
      setSelectedTemplateId(createdRes.data.id)
      addToast({ type: 'success', title: t('common.success'), message: t('hiringProcess.messages.templateCreated', 'Template created') })
    } catch (error: any) {
      const msg = error?.response?.data?.detail || error?.message || String(error)
      addToast({ type: 'error', title: t('common.error'), message: t('hiringProcess.messages.saveError', { error: msg }) })
    } finally {
      setSavingTemplate(false)
    }
  }

  const handleEditStep = (index: number) => {
    const step = steps[index]
    if (isStepImmutable(step.step_type)) {
      addToast({
        type: 'warning',
        title: t('common.warning'),
        message: t('hiringProcess.tooltips.immutableStep', 'This step cannot be modified'),
      })
      return
    }
    setEditingStepIndex(index)
    setShowStepModal(true)
  }

  const handleStepModalSuccess = async (stepData: HiringStep, positionOrder?: number) => {
    if (editingStepIndex !== null) {
      const step = steps[editingStepIndex]
      
      if (!step.id) {
        addToast({ type: 'error', title: t('common.error'), message: t('hiringProcess.errors.missingStepId') })
        return
      }
      
      try {
        const updateData = {
          name: stepData.name.trim(),
          description: stepData.description?.trim() || '',
          color: stepData.color || DEFAULT_STEP_COLOR,
          step_type: stepData.step_type || DEFAULT_STEP_TYPE,
          is_required: stepData.is_required !== undefined ? stepData.is_required : true,
          estimated_duration: stepData.estimated_duration || 1,
          is_active: stepData.is_active !== undefined ? stepData.is_active : true,
          template: step.template || template?.id
          // Note: order is excluded - it should only be changed via reorder action
        }
        
        await hiringProcessApi.updateStep(step.id, updateData)
        
        // Reload step with actions
        const updatedStepResponse = await hiringProcessApi.getStep(step.id)
        const actionsResponse = await hiringProcessApi.getActions(step.id)
        
        // Handle different response formats
        let actions: StepAction[] = []
        const responseData = actionsResponse.data as any
        if (Array.isArray(responseData)) {
          actions = ensureActionsArray(responseData)
        } else if (responseData?.results && Array.isArray(responseData.results)) {
          actions = ensureActionsArray(responseData.results)
        } else if (Array.isArray(actionsResponse)) {
          actions = ensureActionsArray(actionsResponse as any)
        }
        
        const updatedStep = {
          ...updatedStepResponse.data,
          actions,
          action_count: actions.length
        }
        
        // Always update the step - inactive steps are kept visible in management but won't appear in workflow
        setSteps(prev => prev.map((s, i) => 
          i === editingStepIndex ? updatedStep : s
        ))
        setEditingStepIndex(null)
        setShowStepModal(false)
        
        if (updatedStep.is_active === false) {
          addToast({ 
            type: 'success', 
            title: t('common.success'), 
            message: t('hiringProcess.messages.stepUpdatedInactive', 'Step updated. Inactive steps won\'t appear in the hiring workflow.') 
          })
        } else {
          addToast({ type: 'success', title: t('common.success'), message: t('hiringProcess.messages.stepUpdated') })
        }
      } catch (error: any) {
        const errorMessage = error.response?.data?.detail || error.response?.data?.non_field_errors?.[0] || error.message
        addToast({ type: 'error', title: t('common.error'), message: t('hiringProcess.messages.stepUpdateError', { error: errorMessage }) })
      }
    } else {
      // New step creation
      await handleNewStepSuccess(stepData, positionOrder)
    }
  }

  const handleNewStepSuccess = async (stepData: HiringStep, positionOrder?: number) => {
    if (!template) {
      addToast({ type: 'error', title: t('common.error'), message: t('hiringProcess.errors.noTemplate') })
      return
    }
    
    // Prevent duplicate custom names; allow duplicate categories when using default names
    const isCustom = isResolvedStepCustom(stepData, t)
    if (isDuplicateNewStep(steps, stepData.step_type || DEFAULT_STEP_TYPE, stepData.name, isCustom)) {
      addToast({
        type: 'error',
        title: t('common.error'),
        message: t(
          'hiringProcess.errors.duplicateStepType',
          'A step with the same name already exists in this hiring process.'
        ),
      })
      return
    }
    
    try {
      const newStep: any = {
        template: template.id,
        name: stepData.name.trim(),
        description: stepData.description?.trim() || '',
        step_type: stepData.step_type || DEFAULT_STEP_TYPE,
        // Order will be auto-calculated by backend if not provided
        color: stepData.color || DEFAULT_STEP_COLOR,
        is_required: stepData.is_required !== undefined ? stepData.is_required : false,
        estimated_duration: stepData.estimated_duration || 1,
        is_active: stepData.is_active !== undefined ? stepData.is_active : true
      }
      // Enforce selected insertion slot at creation time.
      if (positionOrder != null) {
        newStep.order = positionOrder
      }
      
      const response = await hiringProcessApi.createStep(newStep)

      const resolvedTemplateId = response.data.template ?? template.id
      if (resolvedTemplateId !== template.id) {
        setSelectedTemplateId(resolvedTemplateId)
        await loadTemplate(resolvedTemplateId)
        setEditingStepIndex(null)
        setShowStepModal(false)
        addToast({ type: 'success', title: t('common.success'), message: t('hiringProcess.messages.stepCreated') })
        return
      }
      
      // If a position was chosen, reorder the step after creation to avoid order conflicts
      if (positionOrder != null && response?.data?.id) {
        try {
          await hiringProcessApi.reorderStep(response.data.id, positionOrder)
        } catch (reorderError: any) {
          const reorderMsg =
            reorderError?.response?.data?.detail ||
            reorderError?.response?.data?.non_field_errors?.[0] ||
            reorderError?.message
          addToast({
            type: 'error',
            title: t('common.error'),
            message: t('hiringProcess.messages.stepOrderError', { error: reorderMsg || 'Order update failed' })
          })
        }
      }
      const actionsResponse = await hiringProcessApi.getActions(response.data.id)
      
      // Handle different response formats
      let actions: StepAction[] = []
      const responseData = actionsResponse.data as any
      if (Array.isArray(responseData)) {
        actions = ensureActionsArray(responseData)
      } else if (responseData?.results && Array.isArray(responseData.results)) {
        actions = ensureActionsArray(responseData.results)
      } else if (Array.isArray(actionsResponse)) {
        actions = ensureActionsArray(actionsResponse as any)
      }
      
      const newStepWithActions: EditableStep = {
        ...response.data,
        actions,
        action_count: actions.length
      }
      setSteps(prev => [...prev, newStepWithActions])
      setEditingStepIndex(null)
      setShowStepModal(false)
      loadTemplates({ silent: true })
      addToast({ type: 'success', title: t('common.success'), message: t('hiringProcess.messages.stepCreated') })
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || error.response?.data?.non_field_errors?.[0] || error.message
      addToast({ type: 'error', title: t('common.error'), message: t('hiringProcess.messages.stepCreateError', { error: errorMessage }) })
    }
  }

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event

    if (!over || active.id === over.id) {
      return
    }

    const oldIndex = steps.findIndex((step) => step.id === active.id)
    const newIndex = steps.findIndex((step) => step.id === over.id)

    if (oldIndex === -1 || newIndex === -1) {
      return
    }

    // Only prevent reordering if the step being moved is immutable
    // AND disallow moving any step into the first three locked positions.
    const movedStep = steps[oldIndex]
    if (isStepImmutable(movedStep.step_type)) {
      addToast({
        type: 'warning',
        title: t('common.warning'),
        message: t('hiringProcess.tooltips.cannotReorderImmutable', 'Locked steps cannot be reordered'),
      })
      return
    }

    // First three steps (initial, scanning, preselection) are locked to positions 1–3.
    if (newIndex <= 2) {
      addToast({
        type: 'warning',
        title: t('common.warning'),
        message: t('hiringProcess.tooltips.cannotMoveIntoLockedRegion', 'Steps cannot be moved before the first three locked steps'),
      })
      return
    }

    // Optimistically update UI
    const newSteps = arrayMove(steps, oldIndex, newIndex)
    const updatedSteps = newSteps.map((step, index) => ({
      ...step,
      order: index + 1
    }))
    
    setSteps(updatedSteps)

    const currentTemplateId = selectedTemplateId ?? template?.id ?? null

    try {
      const movedStep = updatedSteps[newIndex]
      // Send the new order (1-based index)
      await hiringProcessApi.reorderStep(movedStep.id, newIndex + 1)
      
      // Reload the same template to keep local state aligned without switching templates
      if (currentTemplateId != null) {
        await loadTemplate(currentTemplateId)
      }
      addToast({ type: 'success', title: t('common.success'), message: t('hiringProcess.messages.stepOrderUpdated') })
    } catch (error: any) {
      // Revert on error
      if (currentTemplateId != null) {
        await loadTemplate(currentTemplateId)
      }
      
      // Extract error message
      let errorMessage = error.message || 'Unknown error'
      if (error.response?.data) {
        if (typeof error.response.data === 'string') {
          errorMessage = error.response.data
        } else if (error.response.data.detail) {
          errorMessage = error.response.data.detail
        } else if (error.response.data.error) {
          errorMessage = error.response.data.error
        } else if (error.response.data.non_field_errors?.[0]) {
          errorMessage = error.response.data.non_field_errors[0]
        } else {
          errorMessage = JSON.stringify(error.response.data)
        }
      }
      
      addToast({ 
        type: 'error', 
        title: t('common.error'), 
        message: t('hiringProcess.messages.stepOrderError', { error: errorMessage }) 
      })
    }
  }

  const handleDeleteStep = async (index: number) => {
    const step = steps[index]
    if (step?.is_deletable === false) {
      addToast({
        type: 'warning',
        title: t('common.warning'),
        message: t('hiringProcess.tooltips.cannotDeleteMandatory', 'Default steps cannot be deleted'),
      })
      return
    }

    setStepDeleteTarget(step)
  }

  const confirmDeleteStep = async () => {
    if (!stepDeleteTarget) return

    try {
      setStepDeleteLoading(true)
      await hiringProcessApi.deleteStep(stepDeleteTarget.id)
      setSteps(prev => prev.filter(step => step.id !== stepDeleteTarget.id))
      setEditingStepIndex(null)
      setShowStepModal(false)
      addToast({ type: 'success', title: t('common.success'), message: t('hiringProcess.messages.stepDeleted') })
      await loadTemplates({ silent: true })
      if (stepDeleteTarget.template) {
        setSelectedTemplateId(stepDeleteTarget.template)
        await loadTemplate(stepDeleteTarget.template)
      } else if (template?.id) {
        await loadTemplate(template.id)
      }
      setStepDeleteTarget(null)
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.detail ||
        error?.response?.data?.non_field_errors?.[0] ||
        (typeof error?.response?.data === 'string' ? error.response.data : null) ||
        error?.message ||
        'Unknown error'
      addToast({
        type: 'error',
        title: t('common.error'),
        message: t('hiringProcess.messages.stepDeleteError', { error: errorMessage }),
      })
    } finally {
      setStepDeleteLoading(false)
    }
  }

  const handleAddStep = () => {
    if (!template) {
      addToast({ type: 'error', title: t('common.error'), message: t('hiringProcess.errors.noTemplate') })
      return
    }
    
    // Open modal for creating new step (don't create immediately)
    setEditingStepIndex(null)
    setShowStepModal(true)
  }

  const handleAddAction = (stepId: number) => {
    setCurrentStepId(stepId)
    setEditingAction(null)
    setShowActionModal(true)
  }

  const handleEditAction = (stepId: number, action: StepAction) => {
    setCurrentStepId(stepId)
    setEditingAction(action)
    setShowActionModal(true)
  }

  const handleDeleteAction = (stepId: number, actionId: number) => {
    const step = steps.find(s => s.id === stepId)
    const stepActions = ensureActionsArray(step?.actions)
    const action = stepActions.find(a => a.id === actionId)

    if (!action) {
      addToast({
        type: 'error',
        title: t('common.error'),
        message: t('hiringProcess.errors.actionNotFound', 'Unable to find this action'),
      })
      return
    }

    setActionDeleteTarget({ stepId, action })
  }

  const confirmDeleteAction = async () => {
    if (!actionDeleteTarget) {
      return
    }

    const { stepId, action } = actionDeleteTarget

    try {
      setActionDeleteLoading(true)
      await hiringProcessApi.deleteAction(action.id)

      const actionsResponse = await hiringProcessApi.getActions(stepId)

      // Handle different response formats
      let actions: StepAction[] = []
      const responseData = actionsResponse.data as any
      if (Array.isArray(responseData)) {
        actions = ensureActionsArray(responseData)
      } else if (responseData?.results && Array.isArray(responseData.results)) {
        actions = ensureActionsArray(responseData.results)
      } else if (Array.isArray(actionsResponse)) {
        actions = ensureActionsArray(actionsResponse as any)
      }

      setSteps(prev => prev.map(step =>
        step.id === stepId
          ? { ...step, actions: [...actions], action_count: actions.length }
          : step
      ))

      addToast({ type: 'success', title: t('common.success'), message: t('hiringProcess.messages.actionDeleted') })
      setActionDeleteTarget(null)
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || error.response?.data?.non_field_errors?.[0] || error.message
      addToast({ type: 'error', title: t('common.error'), message: t('hiringProcess.messages.actionDeleteError', { error: errorMessage }) })
    } finally {
      setActionDeleteLoading(false)
    }
  }

  const handleActionSuccess = async (actionData: StepAction) => {
    if (!currentStepId) return

    try {
      if (editingAction) {
        const updateData = {
          step: currentStepId, // Include step to satisfy backend requirement
          name: actionData.name,
          description: actionData.description,
          action_type: actionData.action_type,
          trigger_type: actionData.trigger_type,
          is_active: actionData.is_active,
          is_required: actionData.is_required,
          delay_hours: actionData.delay_hours,
          expires_hours: actionData.expires_hours,
          configuration: actionData.configuration || {},
          order: actionData.order || editingAction.order
        }
        await hiringProcessApi.updateAction(editingAction.id, updateData)
      } else {
        // Backend will auto-calculate order, so we don't need to fetch or calculate it
        const newActionData = {
          name: actionData.name,
          description: actionData.description || '',
          action_type: actionData.action_type,
          trigger_type: actionData.trigger_type || 'manual',
          is_active: actionData.is_active !== undefined ? actionData.is_active : true,
          is_required: actionData.is_required !== undefined ? actionData.is_required : false,
          delay_hours: actionData.delay_hours || 0,
          expires_hours: actionData.expires_hours !== undefined ? actionData.expires_hours : null,
          configuration: actionData.configuration || {},
          step: currentStepId
          // Order will be auto-calculated by backend
        }
        await hiringProcessApi.createAction(newActionData)
      }

      // Reload step with updated actions
      const actionsResponse = await hiringProcessApi.getActions(currentStepId)
      
      // Handle different response formats (direct array or wrapped in data)
      let actions: StepAction[] = []
      const responseData = actionsResponse.data as any
      if (Array.isArray(responseData)) {
        actions = ensureActionsArray(responseData)
      } else if (responseData?.results && Array.isArray(responseData.results)) {
        // Handle paginated response
        actions = ensureActionsArray(responseData.results)
      } else if (Array.isArray(actionsResponse)) {
        // Response is directly an array
        actions = ensureActionsArray(actionsResponse as any)
      }
      
      // Update state with new actions - create new array/objects to ensure React detects changes
      setSteps(prev => {
        const updated = prev.map(step => {
          if (step.id === currentStepId) {
            return { 
              ...step, 
              actions: [...actions], // Create new array to trigger React re-render
              action_count: actions.length 
            }
          }
          return step
        })
        return updated
      })
      
      addToast({ 
        type: 'success', 
        title: t('common.success'), 
        message: editingAction ? t('hiringProcess.messages.actionUpdated') : t('hiringProcess.messages.actionCreated')
      })
      setShowActionModal(false)
      setEditingAction(null)
      setCurrentStepId(null)
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || 
                          error.response?.data?.non_field_errors?.[0] ||
                          (typeof error.response?.data === 'object' && Object.keys(error.response?.data).length > 0 
                            ? JSON.stringify(error.response?.data) 
                            : error.message)
      addToast({ 
        type: 'error', 
        title: t('common.error'), 
        message: t('hiringProcess.messages.actionSaveError', { error: errorMessage }) 
      })
    }
  }

  if (loading) {
    return (
      <div className="flex flex-1 flex-col">
        <div className="flex items-center justify-center flex-1">
          <div className="text-center space-y-3">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary mx-auto"></div>
            <p className="text-sm text-muted-foreground">{t('common.loading', 'Loading...')}</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-6 max-[1149px]:px-4 max-[1149px]:py-4 max-[1149px]:gap-4">
      <PageHeader title={t('hiringProcess.title')} />

      {selectedTemplateId == null ? (
        <>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm text-muted-foreground">
              {canManageTemplates || canChangePipeline
                ? t('hiringProcess.browseTemplatesHint', 'Select a template to manage its hiring steps.')
                : t('hiringProcess.readOnlyNotice', "You can view hiring templates, but you don't have permission to modify them.")}
            </p>
            {canManageTemplates && (
              <Button
                variant="default"
                onClick={handleOpenCreateTemplate}
                className="gap-2 shrink-0 max-[1149px]:h-9 max-[1149px]:text-sm"
              >
                <Plus className="h-4 w-4 max-[1149px]:h-3.5 max-[1149px]:w-3.5" />
                <span>{t('hiringProcess.createProcess', 'Add Template')}</span>
              </Button>
            )}
          </div>

          {templates.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-16 text-center max-[1149px]:py-10">
                <Layers className="h-10 w-10 text-muted-foreground mb-4 max-[1149px]:h-8 max-[1149px]:w-8" />
                <h3 className="text-sm font-medium text-foreground mb-1">{t('hiringProcess.noProcess')}</h3>
                {canManageTemplates && (
                  <>
                    <p className="text-xs text-muted-foreground mb-4 max-w-sm">{t('hiringProcess.createFirstProcess')}</p>
                    <Button onClick={handleOpenCreateTemplate} className="gap-2">
                      <Plus className="h-4 w-4" />
                      {t('hiringProcess.createProcess', 'Add Template')}
                    </Button>
                  </>
                )}
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
              {sortedTemplatesForBrowse.map((tpl) => (
                <TemplatePreviewCard
                  key={tpl.id}
                  template={tpl}
                  onSelect={handleSelectTemplate}
                />
              ))}
            </div>
          )}
        </>
      ) : (
      <Card>
        <CardHeader className="border-b bg-muted/40 max-[1149px]:p-4">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between max-[1149px]:gap-3">
            <div className="flex min-w-0 flex-1 items-center gap-3">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={handleBackToBrowse}
                className="gap-1.5 shrink-0 text-muted-foreground hover:text-foreground"
              >
                <ArrowLeft className="h-4 w-4" />
                <span className="max-[1149px]:hidden">{t('hiringProcess.backToTemplates', 'All templates')}</span>
              </Button>
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <CardTitle className="truncate text-base max-[1149px]:text-sm">{template?.name}</CardTitle>
                  {template?.is_default && (
                    <Badge variant="outline" className="border-muted-foreground/30 bg-muted/60 text-[10px] uppercase tracking-wide text-muted-foreground">
                      {t('common.default', 'Default')}
                    </Badge>
                  )}
                </div>
                {template?.description ? (
                  <p className="mt-0.5 line-clamp-1 text-xs text-muted-foreground">{template.description}</p>
                ) : null}
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2 max-[1149px]:w-full">
              {canManageTemplates && template && !template.is_default && (
                <Button
                  variant="outline"
                  onClick={handleDeleteTemplateClick}
                  className="gap-2 text-destructive hover:bg-destructive/10 hover:text-destructive max-[1149px]:h-9 max-[1149px]:text-sm"
                  title={t('hiringProcess.deleteTemplate', 'Delete template')}
                >
                  <Trash2 className="h-4 w-4 max-[1149px]:h-3.5 max-[1149px]:w-3.5" />
                  <span className="max-[1149px]:text-sm">{t('hiringProcess.deleteTemplate', 'Delete template')}</span>
                </Button>
              )}
              {canCreateSteps && (
                <Button
                  variant="outline"
                  onClick={handleAddStep}
                  disabled={!template || loadingDetail}
                  className="gap-2 max-[1149px]:flex-1 max-[1149px]:h-9 max-[1149px]:text-sm"
                >
                  <Plus className="h-4 w-4 max-[1149px]:h-3.5 max-[1149px]:w-3.5" />
                  <span className="max-[1149px]:text-sm">{t('hiringProcess.addStep')}</span>
                </Button>
              )}
            </div>
          </div>
          {!canChangePipeline && (
            <p className="text-xs text-muted-foreground">
              {t('hiringProcess.readOnlyNotice', "You can view hiring templates, but you don't have permission to modify them.")}
            </p>
          )}
        </CardHeader>
        <CardContent className="p-0 max-[1149px]:p-0">
          {loadingDetail ? (
            <div className="flex items-center justify-center py-16 max-[1149px]:py-10">
              <div className="text-center space-y-3">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
                <p className="text-sm text-muted-foreground">{t('common.loading', 'Loading...')}</p>
              </div>
            </div>
          ) : steps.length === 0 ? (
            <div className="text-center py-12 max-[1149px]:py-8">
              <Target className="h-10 w-10 text-muted-foreground mx-auto mb-4 max-[1149px]:h-8 max-[1149px]:w-8 max-[1149px]:mb-3" />
              <h3 className="text-sm font-medium text-foreground mb-1 max-[1149px]:text-xs">
                {t('hiringProcess.noStepsYet')}
              </h3>
              {canCreateSteps && (
                <>
                  <p className="text-xs text-muted-foreground mb-4 max-[1149px]:text-[10px] max-[1149px]:mb-3">
                    {t('hiringProcess.createFirstStep')}
                  </p>
                  <Button onClick={handleAddStep} className="gap-2 mx-auto max-[1149px]:h-9 max-[1149px]:text-sm">
                    <Plus className="h-4 w-4 max-[1149px]:h-3.5 max-[1149px]:w-3.5" />
                    <span className="max-[1149px]:text-sm">{t('hiringProcess.addStep')}</span>
                  </Button>
                </>
              )}
            </div>
          ) : (
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext items={steps.map((step) => step.id)} strategy={verticalListSortingStrategy}>
                <div className="flex flex-col gap-3 px-4 py-4 max-[1149px]:px-3 max-[1149px]:py-3 max-[1149px]:gap-2">
                  {steps.map((step, index) => (
                    <SortableStep
                      key={step.id}
                      step={step}
                      index={index}
                      onEdit={handleEditStep}
                      onDelete={handleDeleteStep}
                      onAddAction={handleAddAction}
                      onEditAction={handleEditAction}
                      onDeleteAction={handleDeleteAction}
                      canEdit={canManageSteps}
                      canReorder={canReorderSteps}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          )}
        </CardContent>
      </Card>
      )}

      {/* Step Edit / Add Step Modal for existing template */}
      {showStepModal && (() => {
        const isEditing = editingStepIndex !== null

        // When adding a new step, provide position options similar to Kanban Add Step modal
        const sortedSteps = [...steps].sort((a, b) => (a.order || 0) - (b.order || 0))
        const { positionOptions, defaultPosition } = buildStepPositionOptions(sortedSteps, t)

        return (
          <HiringStepModal
            isOpen={showStepModal}
            onClose={() => {
              setShowStepModal(false)
              setEditingStepIndex(null)
            }}
            onSuccess={handleStepModalSuccess}
            step={isEditing ? steps[editingStepIndex!] : null}
            stepTypes={getUserAddStepCategoryOptions(t)}
            stepColors={STEP_COLORS}
            // Only show position selector when adding a new step; editing keeps existing order
            positionOptions={!isEditing ? positionOptions : undefined}
            defaultPosition={!isEditing ? defaultPosition : undefined}
          />
        )
      })()}

      {/* Template Create Modal */}
      {showTemplateModal && (
        <Modal
          isOpen={showTemplateModal}
          onClose={() => {
            if (!savingTemplate) setShowTemplateModal(false)
          }}
          title={t('hiringProcess.createProcess', 'Create Process Template')}
          className="max-w-2xl"
        >
          <div className="space-y-6">
            {/* Step indicator */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm">
                <span
                  className={`h-7 px-3 rounded-full flex items-center ${
                    templateWizardStep === 1 ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
                  }`}
                >
                  1. {t('hiringProcess.template', 'Template')}
                </span>
                <span
                  className={`h-7 px-3 rounded-full flex items-center ${
                    templateWizardStep === 2 ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
                  }`}
                >
                  2. {t('hiringProcess.steps', 'Applications')}
                </span>
              </div>
            </div>

            {templateWizardStep === 1 && (
              <>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    {t('hiringProcess.templateName', 'Template name')} *
                  </label>
                  <Input
                    value={templateForm.name}
                    onChange={(e) => setTemplateForm(prev => ({ ...prev, name: e.target.value }))}
                    placeholder={t('hiringProcess.templateNamePlaceholder', 'e.g. Standard Hiring Process')}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    {t('hiringProcess.templateDescription', 'Description')}
                  </label>
                  <Textarea
                    value={templateForm.description}
                    onChange={(e) => setTemplateForm(prev => ({ ...prev, description: e.target.value }))}
                    placeholder={t('hiringProcess.templateDescriptionPlaceholder', 'Describe this template')}
                    rows={3}
                  />
                </div>

                <div className="flex items-center gap-6">
                  <label className="flex items-center gap-2 text-sm text-foreground">
                    <input
                      type="checkbox"
                      checked={templateForm.is_active}
                      onChange={(e) => setTemplateForm(prev => ({ ...prev, is_active: e.target.checked }))}
                    />
                    {t('hiringProcess.isActive', 'Active')}
                  </label>
                  <label className="flex items-center gap-2 text-sm text-foreground">
                    <input
                      type="checkbox"
                      checked={templateForm.is_default}
                      onChange={(e) => setTemplateForm(prev => ({ ...prev, is_default: e.target.checked }))}
                    />
                    {t('hiringProcess.isDefault', 'Default')}
                  </label>
                </div>

                <div className="flex justify-end gap-2 pt-2">
                  <Button
                    variant="outline"
                    onClick={() => setShowTemplateModal(false)}
                    disabled={savingTemplate}
                  >
                    {t('common.cancel')}
                  </Button>
                  <Button
                    onClick={() => setTemplateWizardStep(2)}
                    disabled={!templateForm.name.trim()}
                  >
                    {t('common.next', 'Next')}
                  </Button>
                </div>
              </>
            )}

            {templateWizardStep === 2 && (
              <>
                <div className="space-y-2">
                  <p className="text-sm font-medium text-foreground">
                    {t('hiringProcess.hiringSteps', 'Hiring Steps')}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {t(
                      'hiringProcess.coreStepsHelp',
                      'These are the first three core applications from the default hiring process. You can add more application steps if needed.'
                    )}
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {newTemplateSteps.map((step, index) => {
                    const isCore = ['initial', 'scanning', 'screening'].includes(step.step_type)
                    return (
                      <button
                        key={`${step.step_type}-${index}`}
                        type="button"
                        onClick={() => {
                          setWizardEditingIndex(index)
                          setShowWizardStepModal(true)
                        }}
                        className={`border rounded-xl p-4 text-left shadow-sm space-y-2 transition ${
                          isCore
                            ? 'bg-muted cursor-default'
                            : 'bg-card hover:bg-muted/60 cursor-pointer'
                        }`}
                      >
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2">
                          <div
                            className="h-8 w-8 rounded-full flex items-center justify-center text-xs font-semibold text-primary-foreground"
                            style={{ backgroundColor: step.color }}
                          >
                            {index + 1}
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-semibold text-foreground truncate">
                              {step.name}
                            </p>
                            <p className="text-[11px] text-muted-foreground">
                              {t(`hiringProcess.stepTypes.${step.step_type}`)}
                            </p>
                          </div>
                        </div>
                        {/* Core steps: just visually muted by background, no 'Mandatory' label */}
                      </div>
                      {step.description && (
                        <p className="text-xs text-muted-foreground line-clamp-3">
                          {step.description}
                        </p>
                      )}
                      </button>
                    )
                  })}
                  <button
                    type="button"
                    onClick={() => setShowWizardStepModal(true)}
                    className="border-dashed border rounded-xl p-4 flex flex-col items-center justify-center text-sm text-muted-foreground hover:bg-muted/40 transition"
                  >
                    <Plus className="h-4 w-4 mb-1" />
                    <span>{t('hiringProcess.addStep', 'Add Application')}</span>
                  </button>
                </div>

                <div className="flex justify-between gap-2 pt-2">
                  <Button
                    variant="outline"
                    onClick={() => setTemplateWizardStep(1)}
                    disabled={savingTemplate}
                  >
                    {t('common.back', 'Back')}
                  </Button>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      onClick={() => setShowTemplateModal(false)}
                      disabled={savingTemplate}
                    >
                      {t('common.cancel')}
                    </Button>
                    <Button
                      onClick={handleCreateTemplate}
                      loading={savingTemplate}
                    >
                      {t('common.create', 'Create')}
                    </Button>
                  </div>
                </div>
              </>
            )}
          </div>
        </Modal>
      )}

      {/* Wizard Add Application Step Modal (for new template) */}
      {showTemplateModal && showWizardStepModal && (
        <HiringStepModal
          isOpen={showWizardStepModal}
          onClose={() => setShowWizardStepModal(false)}
          onSuccess={(stepData) => {
            setNewTemplateSteps(prev => {
              const next = [...prev]
              const mapped = {
                step_type: stepData.step_type,
                name: stepData.name,
                description: stepData.description || '',
                color: stepData.color,
                is_required: stepData.is_required,
                estimated_duration: stepData.estimated_duration,
                is_active: stepData.is_active,
              }
              if (wizardEditingIndex != null && wizardEditingIndex >= 0 && wizardEditingIndex < next.length) {
                next[wizardEditingIndex] = mapped
              } else {
                next.push(mapped)
              }
              return next
            })
            setWizardEditingIndex(null)
            setShowWizardStepModal(false)
          }}
          step={
            wizardEditingIndex != null && wizardEditingIndex >= 0 && wizardEditingIndex < newTemplateSteps.length
              ? {
                  id: Date.now(),
                  template: 0,
                  order: wizardEditingIndex + 1,
                  created_at: new Date().toISOString(),
                  updated_at: new Date().toISOString(),
                  created_by: undefined,
                  actions: [],
                  action_count: 0,
                  ...newTemplateSteps[wizardEditingIndex],
                }
              : null
          }
          stepTypes={getUserAddStepCategoryOptions(t)}
          stepColors={STEP_COLORS}
        />
      )}

      {/* Step Action Modal */}
      {showActionModal && (() => {
        // Define arrays outside JSX to ensure they're always arrays
        const actionTypesArray: Array<{value: string, label: string, icon: string, description: string}> = [
          { value: 'email', label: t('hiringProcess.actionTypes.send_email'), icon: '📧', description: t('hiringProcess.actionDescriptions.send_email') },
          { value: 'calendar', label: t('hiringProcess.actionTypes.schedule_meeting'), icon: '📅', description: t('hiringProcess.actionDescriptions.schedule_meeting') },
        ]
        
        const triggerTypesArray: Array<{value: string, label: string, description: string}> = [
          { value: 'manual', label: t('hiringProcess.triggerTypes.manual'), description: t('hiringProcess.triggerDescriptions.manual') },
          { value: 'automatic', label: t('hiringProcess.triggerTypes.automatic'), description: t('hiringProcess.triggerDescriptions.automatic') },
          { value: 'on_enter', label: t('hiringProcess.triggerTypes.on_enter'), description: t('hiringProcess.triggerDescriptions.on_enter') },
          { value: 'on_exit', label: t('hiringProcess.triggerTypes.on_exit'), description: t('hiringProcess.triggerDescriptions.on_exit') },
          { value: 'on_completion', label: t('hiringProcess.triggerTypes.on_completion'), description: t('hiringProcess.triggerDescriptions.on_completion') },
          { value: 'scheduled', label: t('hiringProcess.triggerTypes.scheduled'), description: t('hiringProcess.triggerDescriptions.scheduled') },
        ]
        
        return (
          <StepActionModal
            isOpen={showActionModal}
            onClose={() => {
              setShowActionModal(false)
              setEditingAction(null)
              setCurrentStepId(null)
            }}
            onSuccess={handleActionSuccess}
            action={editingAction}
            actionTypes={actionTypesArray}
            triggerTypes={triggerTypesArray}
          />
        )
      })()}

      <Modal
        isOpen={Boolean(actionDeleteTarget)}
        onClose={() => {
          if (actionDeleteLoading) return
          setActionDeleteTarget(null)
        }}
        title={t('hiringProcess.modals.deleteActionTitle', 'Delete Action')}
      >
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            {t('hiringProcess.confirmations.deleteAction', { actionName: actionDeleteTarget?.action.name || t('common.thisAction') })}
          </p>
          <div className="flex justify-end space-x-2">
            <Button
              variant="outline"
              onClick={() => setActionDeleteTarget(null)}
              disabled={actionDeleteLoading}
            >
              {t('common.cancel')}
            </Button>
            <Button
              variant="destructive"
              onClick={confirmDeleteAction}
              disabled={actionDeleteLoading}
            >
              {actionDeleteLoading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  {t('common.deleting', 'Deleting...')}
                </>
              ) : (
                t('hiringProcess.deleteActionCta', 'Delete Action')
              )}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Delete step confirmation (same pattern as Email template delete) */}
      {stepDeleteTarget && (
        <Modal
          isOpen={Boolean(stepDeleteTarget)}
          onClose={() => {
            if (stepDeleteLoading) return
            setStepDeleteTarget(null)
          }}
          title={t('hiringProcess.deleteStepTitle', 'Delete step')}
          className="max-[1149px]:max-w-[95vw]"
        >
          <div className="space-y-4 max-[1149px]:space-y-3">
            <p className="text-sm text-muted-foreground max-[1149px]:text-xs">
              {t('hiringProcess.confirmations.deleteStep', {
                stepName: stepDeleteTarget.name,
              })}
            </p>
            <div className="flex justify-end space-x-2 max-[1149px]:flex-col max-[1149px]:space-y-2 max-[1149px]:space-x-0">
              <Button
                variant="outline"
                onClick={() => setStepDeleteTarget(null)}
                disabled={stepDeleteLoading}
                className="max-[1149px]:w-full max-[1149px]:h-9 max-[1149px]:text-sm"
              >
                {t('common.cancel')}
              </Button>
              <Button
                variant="destructive"
                onClick={confirmDeleteStep}
                disabled={stepDeleteLoading}
                className="max-[1149px]:w-full max-[1149px]:h-9 max-[1149px]:text-sm"
              >
                {stepDeleteLoading
                  ? t('common.deleting', 'Deleting...')
                  : t('hiringProcess.deleteStepCta', 'Delete step')}
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {/* Delete template confirmation (same pattern as Email template delete) */}
      {showDeleteTemplateModal && deletingTemplate && (
        <Modal
          isOpen={showDeleteTemplateModal}
          onClose={() => {
            setShowDeleteTemplateModal(false)
            setDeletingTemplate(null)
          }}
          title={t('hiringProcess.deleteTemplate', 'Delete template')}
          className="max-[1149px]:max-w-[95vw]"
        >
          <div className="space-y-4 max-[1149px]:space-y-3">
            <p className="text-sm text-muted-foreground max-[1149px]:text-xs">
              {t('hiringProcess.confirmations.deleteTemplate', { name: deletingTemplate.name })}
            </p>
            <div className="flex justify-end space-x-2 max-[1149px]:flex-col max-[1149px]:space-y-2 max-[1149px]:space-x-0">
              <Button
                variant="outline"
                onClick={() => {
                  setShowDeleteTemplateModal(false)
                  setDeletingTemplate(null)
                }}
                className="max-[1149px]:w-full max-[1149px]:h-9 max-[1149px]:text-sm"
              >
                {t('common.cancel')}
              </Button>
              <Button
                variant="destructive"
                onClick={handleConfirmDeleteTemplate}
                className="max-[1149px]:w-full max-[1149px]:h-9 max-[1149px]:text-sm"
              >
                {t('hiringProcess.deleteTemplate', 'Delete template')}
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  )
}
